import React, { useState, useEffect, useMemo } from 'react';
import { tokens } from '@udemy/styles';
export { matchMediaMock } from './match-media-mock';
export { matchMediaQueryListMock, mediaQueryMock } from './match-media-query-list-mock';
// NOTE: breakpoint- prefixes are deprecated.
const tokenPrefixes = /^breakpoint-|viewport-/;
// Record of token suffixes and their corresponding media query strings
// e.g., {'md-max': '(max-width: 61.25rem)', ...}
const shorthandQueries = Object.keys(tokens).filter(tokenKey => tokenKey.match(tokenPrefixes)).reduce((queries, tokenKey) => {
  const tokenSuffix = tokenKey.replace(tokenPrefixes, '');
  const condition = tokenSuffix.endsWith('-max') ? 'max-width' : 'min-width';
  const width = tokens[tokenKey];
  return {
    ...queries,
    [tokenSuffix]: `(${condition}: ${width})`
  };
}, {});

// Converts a shorthand breakpoint key to a media query string, or passes through
// a custom media query string.
export const normalizeMediaQuery = mediaQueryStringOrKey => {
  var _ref;
  // @ts-expect-error key may not be a property of shorthandQueries
  return (_ref = shorthandQueries[mediaQueryStringOrKey]) !== null && _ref !== void 0 ? _ref : mediaQueryStringOrKey;
};

/**
 * A hook that wraps the {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia | matchMedia API}.
 *
 * @param mediaQueryStringOrKey media query string or a breakpoint key
 * @returns result of media query match, or `null` server-side and on initial client-side render
 *
 * @remarks
 * Receives a media query string as the input and returns a boolean indicating
 * whether the media query string is matched.
 *
 * A media query string is either:
 * - a shorthand breakpoint key from the Design System {@link @udemy/styles | global tokens}
 *   without the `viewport-` prefix, e.g. `'md-max'`. This corresponds to `(max-width: @viewport-md-max)`.
 * - a media query string, e.g. `'(max-width: 42rem)'`. Note the parentheses. This syntax
 *   should only be used for custom media queries.
 *
 * This hook always returns `null` during server-side rendering because the matchMedia API does
 * not exist outside the context of a user agent. To avoid React component hydration mismatches,
 * this hook also returns `null` during the initial client-side render.
 */
export const useMatchMedia = mediaQueryStringOrKey => {
  const mediaQueryString = normalizeMediaQuery(mediaQueryStringOrKey);
  const mediaQueryList = useMemo(() => {
    var _window$matchMedia, _window;
    return typeof window !== 'undefined' && ((_window$matchMedia = (_window = window).matchMedia) === null || _window$matchMedia === void 0 ? void 0 : _window$matchMedia.call(_window, mediaQueryString));
  }, [mediaQueryString]);

  // Return null on server and on initial render on client
  const [matches, setMatches] = useState(null);
  const handleChange = event => {
    setMatches(event.matches);
  };
  useEffect(() => {
    if (mediaQueryList) {
      setMatches(mediaQueryList.matches);

      // Use contemporary MediaQueryList API if available, fall back
      // to deprecated functions if necessary.
      if (mediaQueryList.addEventListener) {
        mediaQueryList.addEventListener('change', handleChange);
      } else {
        mediaQueryList.addListener(handleChange);
      }
      return () => {
        if (mediaQueryList.removeEventListener) {
          mediaQueryList.removeEventListener('change', handleChange);
        } else {
          mediaQueryList.removeListener(handleChange);
        }
      };
    }
  }, [mediaQueryString, mediaQueryList]);
  return matches;
};

/**
 * An HOC that provides boolean props corresponding to media query strings.
 *
 * @param mediaQueryStringMap an object binding prop names to media query strings
 *
 * @remarks
 * This HOC provides booleans corresponding to the keys of the mediaQueryStringMap to
 * its wrapped component.
 *
 * @see {@link useMatchMedia} for details on how the media query strings work. `withMatchMedia`
 * is implemented with `useMatchMedia`.
 */
export const withMatchMedia = mediaQueryStringMap => {
  return WrappedComponent => props => {
    const mqlMap = Object.entries(mediaQueryStringMap).reduce((acc, [key, mediaQueryString]) => ({
      ...acc,
      [key]: useMatchMedia(mediaQueryString)
    }), {});
    return /*#__PURE__*/React.createElement(WrappedComponent, Object.assign({}, props, mqlMap));
  };
};
//# sourceMappingURL=index.js.map